/* =============================================================================
 *  TerminalNewJobModal
 *
 *  Terminal-native equivalent of `NewJobModal`. Same data contract — same
 *  Firestore writes, same `onJobCreated(job)` callback signature, same
 *  `initialCustomerId` / `initialDescription` pre-fill — but rendered as a
 *  monospace bracketed-label form so it doesn't pull Bootstrap form
 *  components into the terminal feed.
 *
 *  Used by `TerminalEmailFeed` (Link to Job → "Create new job…") and any
 *  other terminal-theme caller that needs an inline new-job creation flow.
 *
 *  Notes
 *   • Same field set + validation rules as the default modal: jobLabel,
 *     customer(s), description, dates, hours, status. Validation is
 *     hand-rolled (no Yup) to keep the terminal bundle small.
 *   • `jobLabel` is prefixed with the customer's company name on save —
 *     parity with the default modal so jobs created from email and from
 *     the legacy UI both end up with consistent labels.
 *   • Multi-customer is supported (some jobs span more than one client),
 *     same as the default modal.
 * ============================================================================= */

import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { collection, doc, getDocs, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { Job, JobStatus, NewJobModalProps, createJobWithDefaults } from '../../pages/job-summary/Interfaces';
import './TerminalNewJobModal.scss';

interface CustomerRow {
  id: string;
  name: string;
  company?: string;
  email?: string;
}

const STATUS_OPTIONS: { value: JobStatus; label: string }[] = [
  { value: 'Pending',                  label: 'Pending Start' },
  { value: 'Open',                     label: 'Ready to Start' },
  { value: 'Active',                   label: 'In Progress' },
  { value: 'Awaiting Client Response', label: 'Awaiting Response' },
  { value: 'Overdue',                  label: 'Overdue' },
  { value: 'Closed',                   label: 'Completed' },
  { value: 'Cancelled',                label: 'Cancelled' },
  { value: 'Archived',                 label: 'Archived' },
];

const todayISO = (): string => new Date().toISOString().slice(0, 10);

const TerminalNewJobModal: React.FC<NewJobModalProps> = ({
  show, onClose, initialCustomerId, initialDescription, onJobCreated,
}) => {
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Form state — kept simple. No yup; manual validation on submit.
  const [creationDate, setCreationDate] = useState<string>(todayISO());
  const [jobLabel,     setJobLabel]     = useState<string>('');
  const [customerIds,  setCustomerIds]  = useState<string[]>([]);
  const [description,  setDescription]  = useState<string>('');
  const [unbilledHours, setUnbilledHours] = useState<number>(0);
  const [totalHours,    setTotalHours]    = useState<number>(0);
  const [status,       setStatus]       = useState<JobStatus>('Open');
  const [dueDate,      setDueDate]      = useState<string>('');
  const [customerSearch, setCustomerSearch] = useState<string>('');
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Load customers once when the modal mounts (same trigger as default).
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'customers'));
        if (cancelled) return;
        const rows: CustomerRow[] = snap.docs.map((d) => {
          const v = d.data() as any;
          return {
            id: d.id,
            name: v.name || '(unnamed)',
            company: v.company || '',
            email: v.email || '',
          };
        });
        rows.sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setCustomers(rows);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[TerminalNewJobModal] customer load failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [show]);

  // Apply initial values once per open. Dependency reset cleans up
  // previous opens if the modal stays mounted across re-opens.
  const [appliedInitial, setAppliedInitial] = useState(false);
  useEffect(() => {
    if (show && !appliedInitial) {
      if (initialCustomerId) setCustomerIds([initialCustomerId]);
      if (initialDescription) setDescription(initialDescription);
      setAppliedInitial(true);
    }
    if (!show) {
      // Full reset on close so the next open starts fresh.
      setAppliedInitial(false);
      setCreationDate(todayISO());
      setJobLabel('');
      setCustomerIds([]);
      setDescription('');
      setUnbilledHours(0);
      setTotalHours(0);
      setStatus('Open');
      setDueDate('');
      setCustomerSearch('');
      setTouched({});
      setSubmitError(null);
      setSubmitting(false);
    }
  }, [show, appliedInitial, initialCustomerId, initialDescription]);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return customers.slice(0, 12);
    return customers
      .filter((c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.company || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q),
      )
      .slice(0, 24);
  }, [customers, customerSearch]);

  const selectedCustomers = useMemo(
    () => customers.filter((c) => customerIds.includes(c.id)),
    [customers, customerIds],
  );

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!creationDate) e.creationDate = 'creation date required';
    if (!jobLabel.trim()) e.jobLabel = 'job label required';
    if (customerIds.length === 0) e.customerId = 'pick at least one customer';
    if (!description.trim()) e.description = 'description required';
    if (!Number.isFinite(unbilledHours) || unbilledHours < 0) e.unbilledHours = 'must be 0 or more';
    if (!Number.isFinite(totalHours)    || totalHours    < 0) e.totalHours    = 'must be 0 or more';
    if (!dueDate) e.dueDate = 'due date required';
    if (!status) e.status = 'status required';
    return e;
  }, [creationDate, jobLabel, customerIds, description, unbilledHours, totalHours, dueDate, status]);

  const isValid = Object.keys(errors).length === 0;

  const toggleCustomer = (id: string) => {
    setCustomerIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const fieldErr = (key: string): string | null =>
    (touched[key] && errors[key]) ? errors[key] : null;

  const handleSubmit = async () => {
    setTouched({
      creationDate: true, jobLabel: true, customerId: true, description: true,
      unbilledHours: true, totalHours: true, dueDate: true, status: true,
    });
    if (!isValid || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Mirror the default modal: prefix the user-typed jobLabel with the
      // company names of the selected customers so the rendered label
      // makes sense in lists ("Acme Pty - Website Redesign").
      const prefix = selectedCustomers
        .map((c) => c.company || c.name)
        .filter(Boolean)
        .join(' & ');
      const fullLabel = prefix ? `${prefix} - ${jobLabel.trim()}` : jobLabel.trim();

      const baseJob = createJobWithDefaults({
        id: '',
        creationDate,
        customerId: customerIds,
        jobLabel: fullLabel,
        description: description.trim(),
        unbilledHours: Number(unbilledHours) || 0,
        totalHours:    Number(totalHours)    || 0,
        dueDate,
        status,
        lastEdited: todayISO(),
      });
      const { id: _ignored, ...payload } = baseJob;

      const jobRef = doc(collection(db, 'jobs'));
      await setDoc(jobRef, payload);
      const created = { ...payload, id: jobRef.id } as Job;
      onJobCreated?.(created);
      onClose();
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[TerminalNewJobModal] save failed:', err);
      setSubmitError(err?.message || 'save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      contentClassName="term-newjob"
      dialogClassName="term-newjob-dialog"
    >
      <div className="term-newjob__head">
        <span>$ new job</span>
        <button
          type="button"
          className="term-newjob__close"
          onClick={onClose}
          aria-label="Close"
        >[×]</button>
      </div>

      <div className="term-newjob__body">
        {/* — customer (multi) — */}
        <div className="term-newjob__field">
          <label className="term-newjob__label">customer(s)</label>
          {selectedCustomers.length > 0 && (
            <div className="term-newjob__chips">
              {selectedCustomers.map((c) => (
                <span key={c.id} className="term-newjob__chip">
                  {c.name}
                  <button
                    type="button"
                    className="term-newjob__chip-x"
                    onClick={() => toggleCustomer(c.id)}
                    aria-label={`Remove ${c.name}`}
                  >×</button>
                </span>
              ))}
            </div>
          )}
          <input
            type="text"
            className="term-newjob__input"
            value={customerSearch}
            placeholder="search customers..."
            onChange={(e) => setCustomerSearch(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, customerId: true }))}
          />
          <div className="term-newjob__list">
            {filteredCustomers.length === 0 ? (
              <div className="term-newjob__empty">no matches</div>
            ) : filteredCustomers.map((c) => {
              const sel = customerIds.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  className={'term-newjob__row' + (sel ? ' term-newjob__row--active' : '')}
                  onClick={() => toggleCustomer(c.id)}
                >
                  <span>{sel ? '[✓]' : '[ ]'}</span>
                  <span className="term-newjob__row-name">{c.name}</span>
                  {c.company && <span className="term-newjob__row-meta">{c.company}</span>}
                </button>
              );
            })}
          </div>
          {fieldErr('customerId') && <div className="term-newjob__err">{fieldErr('customerId')}</div>}
        </div>

        {/* — job label — */}
        <div className="term-newjob__field">
          <label className="term-newjob__label">job label</label>
          <input
            type="text"
            className="term-newjob__input"
            value={jobLabel}
            placeholder="e.g. Website redesign"
            onChange={(e) => setJobLabel(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, jobLabel: true }))}
          />
          <div className="term-newjob__hint">
            saved as: {selectedCustomers.length > 0
              ? `${selectedCustomers.map((c) => c.company || c.name).join(' & ')} - ${jobLabel || '...'}`
              : jobLabel || '...'}
          </div>
          {fieldErr('jobLabel') && <div className="term-newjob__err">{fieldErr('jobLabel')}</div>}
        </div>

        {/* — description — */}
        <div className="term-newjob__field">
          <label className="term-newjob__label">description</label>
          <textarea
            className="term-newjob__textarea"
            value={description}
            rows={4}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, description: true }))}
          />
          {fieldErr('description') && <div className="term-newjob__err">{fieldErr('description')}</div>}
        </div>

        {/* — dates row — */}
        <div className="term-newjob__row-fields">
          <div className="term-newjob__field">
            <label className="term-newjob__label">creation date</label>
            <input
              type="date"
              className="term-newjob__input"
              value={creationDate}
              onChange={(e) => setCreationDate(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, creationDate: true }))}
            />
            {fieldErr('creationDate') && <div className="term-newjob__err">{fieldErr('creationDate')}</div>}
          </div>
          <div className="term-newjob__field">
            <label className="term-newjob__label">due date</label>
            <input
              type="date"
              className="term-newjob__input"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, dueDate: true }))}
            />
            {fieldErr('dueDate') && <div className="term-newjob__err">{fieldErr('dueDate')}</div>}
          </div>
        </div>

        {/* — hours + status — */}
        <div className="term-newjob__row-fields">
          <div className="term-newjob__field">
            <label className="term-newjob__label">unbilled hours</label>
            <input
              type="number"
              min={0}
              step={0.25}
              className="term-newjob__input"
              value={Number.isFinite(unbilledHours) ? unbilledHours : 0}
              onChange={(e) => setUnbilledHours(parseFloat(e.target.value))}
              onBlur={() => setTouched((t) => ({ ...t, unbilledHours: true }))}
            />
            {fieldErr('unbilledHours') && <div className="term-newjob__err">{fieldErr('unbilledHours')}</div>}
          </div>
          <div className="term-newjob__field">
            <label className="term-newjob__label">total hours</label>
            <input
              type="number"
              min={0}
              step={0.25}
              className="term-newjob__input"
              value={Number.isFinite(totalHours) ? totalHours : 0}
              onChange={(e) => setTotalHours(parseFloat(e.target.value))}
              onBlur={() => setTouched((t) => ({ ...t, totalHours: true }))}
            />
            {fieldErr('totalHours') && <div className="term-newjob__err">{fieldErr('totalHours')}</div>}
          </div>
          <div className="term-newjob__field">
            <label className="term-newjob__label">status</label>
            <select
              className="term-newjob__select"
              value={status}
              onChange={(e) => setStatus(e.target.value as JobStatus)}
              onBlur={() => setTouched((t) => ({ ...t, status: true }))}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {fieldErr('status') && <div className="term-newjob__err">{fieldErr('status')}</div>}
          </div>
        </div>

        {submitError && <div className="term-newjob__err term-newjob__err--top">{submitError}</div>}
      </div>

      <div className="term-newjob__foot">
        <button
          type="button"
          className="term-newjob__btn"
          onClick={onClose}
          disabled={submitting}
        >[cancel]</button>
        <button
          type="button"
          className="term-newjob__btn term-newjob__btn--primary"
          onClick={handleSubmit}
          disabled={submitting || !isValid}
        >{submitting ? '[saving...]' : '[save job]'}</button>
      </div>
    </Modal>
  );
};

export default TerminalNewJobModal;
